// Entry for the 11 fully static service pages.
//
// These pages are plain static HTML (no React rendering, no react-router).
// This entry mounts only two interactive islands into that static markup:
//   1. #auth-menu-root / #auth-menu-root-mobile -- the login/account widget (AuthMenu)
//   2. #consent-root    -- the cookie banner + manager modal
//
// Do NOT import App, the router, or any page component here -- that would
// pull the entire SPA bundle into these lightweight static pages.
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Router } from 'react-router-dom';
import type { Navigator } from 'react-router';
import { AuthProvider } from './context/AuthContext';
import { ConsentProvider, useConsent } from './context/ConsentContext';
import AuthMenu from './components/auth/AuthMenu';
import CookieBanner from './components/CookieBanner';
import CookieManagerModal from './components/CookieManagerModal';
import './lib/supabaseClient';
import './i18n';

// AuthMenu renders a react-router <Link to="/spin">. There is no <RouterProvider>
// on these static pages, so we hand it a minimal navigator that performs a real
// full-page navigation instead of client-side routing.
const staticNavigator: Navigator = {
  push(to) {
    window.location.href = typeof to === 'string' ? to : to.pathname || '/';
  },
  replace(to) {
    window.location.href = typeof to === 'string' ? to : to.pathname || '/';
  },
  go(delta) {
    window.history.go(delta);
  },
  createHref(to) {
    return typeof to === 'string' ? to : to.pathname || '/';
  },
  listen() {
    return () => {};
  },
};

function AuthIsland() {
  return (
    <StrictMode>
      <AuthProvider>
        <Router location={window.location} navigator={staticNavigator}>
          <AuthMenu />
        </Router>
      </AuthProvider>
    </StrictMode>
  );
}

function ConsentIsland() {
  return (
    <StrictMode>
      <ConsentProvider>
        <CookieBanner />
        <CookieManagerModal />
        <ConsentManagerBridge />
      </ConsentProvider>
    </StrictMode>
  );
}

// Lets the static footer's plain "Manage cookies" button (outside the React
// tree) reopen the consent manager by dispatching a DOM event.
function ConsentManagerBridge() {
  const { openManager } = useConsent();
  useEffect(() => {
    const handler = () => openManager();
    window.addEventListener('open-cookie-manager', handler);
    return () => window.removeEventListener('open-cookie-manager', handler);
  }, [openManager]);
  return null;
}

// Mirrors the desktop/mobile dual-mount pattern already used on the homepage
// (see main.tsx's nav-auth-desktop/nav-auth-mobile) since the static navbar
// renders separate desktop and mobile AuthMenu slots.
['auth-menu-root', 'auth-menu-root-mobile'].forEach((id) => {
  const el = document.getElementById(id);
  if (el) createRoot(el).render(<AuthIsland />);
});

const consentRoot = document.getElementById('consent-root');
if (consentRoot) createRoot(consentRoot).render(<ConsentIsland />);
